import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BookOpen, Mic, MessageSquare, ListChecks, PenLine, BookMarked, Globe, Users, TrendingUp, Database, Bot, Eye } from 'lucide-react';
import { fetchAdminStats, fetchDAUStats } from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import VocabularyFillinManager from './VocabularyFillinManager';
import SpeakingExerciseManager from './SpeakingExerciseManager';
import AiScenarioManager from './AiScenarioManager';
import ConversationManager from './ConversationManager';
import TopicFillinQAManager from './TopicFillinQAManager';
import WritingExerciseManager from './WritingExerciseManager';
import StudentProgressManager from './StudentProgressManager';
import DiaryEntriesManager from './DiaryEntriesManager';
import GlobalDictionaryManager from './GlobalDictionaryManager';
import UsersManager from './UsersManager';
import DatabaseManager from './DatabaseManager';
import LessonVisibilityManager from './LessonVisibilityManager';

const TABS = [
  { key: 'lessons', label: 'Lekce', icon: Eye },
  { key: 'vocabulary-fillin', label: 'Slovíčka', icon: BookOpen },
  { key: 'speaking-exercises', label: 'Mluvení', icon: Mic },
  { key: 'conversations', label: 'Dialogy', icon: MessageSquare },
  { key: 'ai-scenarios', label: 'Konverzace s AI', icon: Bot },
  { key: 'topic-fillin-qa', label: 'Doplňování', icon: ListChecks },
  { key: 'writing-exercises', label: 'Psaní', icon: PenLine },
  { key: 'diary-entries', label: 'Deníky', icon: BookMarked },
  { key: 'global-dictionary', label: 'Globální slovník', icon: Globe },
  { key: 'users', label: 'Uživatelé', icon: Users },
  { key: 'student-progress', label: 'Postup studentů', icon: TrendingUp },
  { key: 'database', label: 'Databáze', icon: Database },
];

const AdminDashboard = ({ onBackToLessons }) => {
  const [activeTab, setActiveTab] = useState('vocabulary-fillin');
  const [stats, setStats] = useState(null);
  const [dauStats, setDauStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch admin statistics on component mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const [statsData, dauData] = await Promise.all([
          fetchAdminStats(),
          fetchDAUStats()
        ]);
        setStats(statsData);
        setDauStats(dauData);
        setError(null);
      } catch (err) {
        console.error('Error fetching admin stats:', err);
        setError('Failed to load admin statistics');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  // Prepare chart data
  const chartData = dauStats?.daily_active_users?.map(day => ({
    date: new Date(day.date).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' }),
    active_users: day.active_users
  })) || [];

  if (loading && !stats) {
    return <Loading />;
  }

  if (error && !stats) {
    return <Error message={error} />;
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Tlačítko zpět */}
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={onBackToLessons}
            style={{
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '14px 24px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.borderColor = '#ccc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = '#e0e0e0';
            }}
          >
            <span style={{ fontSize: '1rem', fontWeight: '500', color: '#1a1a1a' }}>← Zpět na lekce</span>
          </button>
        </div>

        <h1 style={{
          fontSize: '1.8rem',
          fontWeight: 'bold',
          marginBottom: '30px',
          color: '#2c3e50',
          textAlign: 'center'
        }}>
          Administrace — správa cvičení
        </h1>

        {/* Stats Summary */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              borderTop: '3px solid #28a745',
              transition: 'all 0.2s ease',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
            >
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '1rem' }}>Slovíčka</h3>
              <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#28a745' }}>{stats.vocabulary_fillin_exercises}</p>
            </div>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              borderTop: '3px solid #dc3545',
              transition: 'all 0.2s ease',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
            >
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '1rem' }}>Mluvení</h3>
              <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#dc3545' }}>{stats.speaking_exercises}</p>
            </div>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              textAlign: 'center',
              borderTop: '3px solid #343a40',
              transition: 'all 0.2s ease',
              cursor: 'default'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
            >
              <h3 style={{ margin: '0 0 10px 0', color: '#666', fontSize: '1rem' }}>Celkem cvičení</h3>
              <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#343a40' }}>{stats.total_exercises}</p>
            </div>
          </div>
        )}

        {/* DAU Chart */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '30px'
        }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50', fontSize: '1.2rem' }}>
            Denni aktivni uzivatele (poslednich 7 dni)
          </h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [`${value}`, 'Aktivnich uzivatelu']}
                  labelStyle={{ color: '#374151' }}
                />
                <Bar dataKey="active_users" fill="#0d6efd" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ textAlign: 'center', color: '#666', padding: '40px 0' }}>
              Zatim zadna data o aktivite uzivatelu
            </p>
          )}
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '2px',
          marginBottom: '20px'
        }}>
          {TABS.map(({ key, label, icon: Icon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderBottom: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                  backgroundColor: isActive ? '#eff6ff' : 'transparent',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: isActive ? '600' : '500',
                  color: isActive ? '#3b82f6' : '#6b7280',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '8px 8px 0 0'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
        <div style={{ height: '2px', backgroundColor: '#e5e7eb', marginTop: '-2px', marginBottom: '20px' }} />

        {/* Tab Content */}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {activeTab === 'lessons' && <LessonVisibilityManager />}
          {activeTab === 'vocabulary-fillin' && <VocabularyFillinManager />}
          {activeTab === 'speaking-exercises' && <SpeakingExerciseManager />}
          {activeTab === 'conversations' && <ConversationManager />}
          {activeTab === 'ai-scenarios' && <AiScenarioManager />}
          {activeTab === 'topic-fillin-qa' && <TopicFillinQAManager />}
          {activeTab === 'writing-exercises' && <WritingExerciseManager />}
          {activeTab === 'diary-entries' && <DiaryEntriesManager />}
          {activeTab === 'global-dictionary' && <GlobalDictionaryManager />}
          {activeTab === 'users' && <UsersManager />}
          {activeTab === 'student-progress' && <StudentProgressManager />}
          {activeTab === 'database' && <DatabaseManager onPurgeComplete={() => {
            fetchAdminStats().then(setStats).catch(() => {});
          }} />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
